import { getBackendConfig } from '../config/backend';

/**
 * Service for tracking metrics and analytics events
 * All tracking is non-blocking and fire-and-forget
 */
export class MetricsService {
    private apiBaseUrl: string;

    constructor() {
        const config = getBackendConfig();
        this.apiBaseUrl = config.apiBaseUrl;
    }

    /**
     * Track a playback event (track or snippet play)
     * This is a non-blocking, fire-and-forget operation
     * 
     * @param audioFileId - The audio file/track ID
     * @param snippetId - The snippet ID (null for track plays)
     * @param eventType - 'track_play' or 'snippet_play'
     * @param userId - Optional user ID (for authenticated users)
     * @param idToken - Optional Firebase ID token (for authenticated requests)
     */
    async trackPlayback(
        audioFileId: number,
        snippetId: number | null,
        eventType: 'track_play' | 'snippet_play',
        userId?: number | null,
        idToken?: string | null
    ): Promise<void> {
        try {
            // Get auth token if user is authenticated
            let headers: HeadersInit = {
                'Content-Type': 'application/json',
            };

            // Add auth token if provided
            if (idToken) {
                headers['Authorization'] = `Bearer ${idToken}`;
            }

            // Send event (don't await - fire and forget)
            const payload = {
                audioFileId,
                snippetId,
                eventType
            };
            
            console.log('📊 Sending playback event:', payload, 'to', `${this.apiBaseUrl}/api/events/playback`);
            
            fetch(`${this.apiBaseUrl}/api/events/playback`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            })
            .then(response => {
                console.log('📊 Metrics tracking response:', response.status, response.statusText);
                if (!response.ok) {
                    console.warn('📊 Metrics tracking returned non-OK status:', response.status);
                }
                return response.json();
            })
            .then(data => {
                console.log('📊 Metrics tracking success:', data);
            })
            .catch((error) => {
                // Log error but don't break the app
                console.warn('❌ Metrics tracking failed (non-critical):', error);
            });
        } catch (error) {
            // Silently fail - metrics tracking should not break the app
            console.warn('Metrics tracking error (non-critical):', error);
        }
    }

    /**
     * Track track playback
     */
    async trackTrackPlay(audioFileId: number, userId?: number | null, idToken?: string | null): Promise<void> {
        return this.trackPlayback(audioFileId, null, 'track_play', userId, idToken);
    }

    /**
     * Track snippet playback
     */
    async trackSnippetPlay(audioFileId: number, snippetId: number, userId?: number | null, idToken?: string | null): Promise<void> {
        return this.trackPlayback(audioFileId, snippetId, 'snippet_play', userId, idToken);
    }
}

// Export singleton instance
export const metricsService = new MetricsService();

